/**
 * Subscribes to the change of data-config-id attribute on provided elements.
 * - Returns each element which receives data-config-id
 * - Remaining elements without data-config-id are returned after 1000 ms passed
 */
export function initConfigIdObserver(
  elements: Element[],
  onConfigIdFound: (element: Element) => void,
  onTimeout: (remainingElements: Element[]) => void,
  timeoutMs: number = 2000
) {
  if (!elements || elements.length === 0) {
    onTimeout([]);
  }

  const remaining = new Set<Element>(elements);

  // checks if custom id is already present on the element
  elements.forEach((element) => {
    const value = element.getAttribute("data-config-id");
    if (value) {
      onConfigIdFound(element);
      remaining.delete(element);
    }
  });

  if (remaining.size === 0) return;

  const observers = new Map<Element, MutationObserver>();

  const finish = () => {
    onTimeout(Array.from(remaining));
    observers.forEach((obs) => obs.disconnect());
    remaining.clear();
  };

  const timeout = setTimeout(() => finish(), timeoutMs);

  elements.forEach((element) => {
    const observer = new MutationObserver(() => {
      const value = element.getAttribute("data-config-id");

      if (value) {
        onConfigIdFound(element);
        remaining.delete(element);
        observer.disconnect();

        if (remaining.size === 0) {
          clearTimeout(timeout);
          finish();
        }
      }
    });

    observer.observe(element, { attributes: true });
    observers.set(element, observer);
  });
}
